import { createSlice, createAsyncThunk, type PayloadAction } from "@reduxjs/toolkit";
import { containerOrderAPI, userSettingsAPI } from "../../services/api";
import type { Column } from "../../types";
import type {
  ContainerLineOrderRef,
  ContainerLinesEntry,
  ContainerLineStatus,
  ContainerOrder,
  ContainerOrderCancelPreview,
  ContainerOrderCopyData,
  ContainerOrderCopySource,
  ContainerOrderFilters,
  ContainerOrderFormData,
  ContainerOrderLine,
  ContainerOrderProgress,
  ContainerLineFormData,
  ContainerOrdersState,
  GenerateLinesPayload,
  ServiceInvoiceLocks,
} from "../../types/containerOrders";
import { STARTED_LINE_STATUSES } from "../../types/containerOrders";
import type { ContainerMessaging } from "../../types/containerMessages";

const initialState: ContainerOrdersState = {
  orders: [],
  current: null,
  currentServiceLocks: {},
  linesByOrder: {},
  containerMatchOrderIds: [],
  columns: [],
  columnsLoaded: false,
  columnsLoading: false,
  loading: false,
  saving: false,
  generating: false,
  error: null,
  pagination: { page: 1, limit: 20, total: 0, pages: 0 },
};

const emptyLinesEntry = (): ContainerLinesEntry => ({
  lines: [],
  progress: null,
  loadedPages: 0,
  total: 0,
  matchedTotal: null,
  loading: false,
  error: null,
  staleTick: 0,
});

/**
 * Tell an open panel its rows are out of date.
 *
 * Only for orders somebody actually expanded — a bucket is not created here,
 * because inventing one for every order a bulk action touched would have the
 * store holding empty entries for rows nobody has looked at.
 */
const invalidateLines = (state: ContainerOrdersState, orderId?: string) => {
  const entry = orderId ? state.linesByOrder[orderId] : undefined;
  if (entry) entry.staleTick += 1;
};

/**
 * The bucket for one order, created on demand.
 *
 * Every lines reducer goes through this rather than indexing directly, so a
 * response that arrives for an order the user has since collapsed lands in its
 * own bucket instead of overwriting whichever one happens to be open.
 */
const entryFor = (
  state: ContainerOrdersState,
  orderId: string,
): ContainerLinesEntry => {
  if (!state.linesByOrder[orderId]) {
    state.linesByOrder[orderId] = emptyLinesEntry();
  }
  return state.linesByOrder[orderId];
};

/**
 * Keep an order's tallies in step with a container that just changed, without
 * asking the server again.
 *
 * The progress bar is a count per status and the server derives it the same
 * way, so a container moving between statuses is a decrement and an increment —
 * arithmetic the store can do itself. Doing it here is what lets an ordinary
 * edit cost the one request that saved it: the row comes back in the response
 * and the bar is corrected from it, instead of both being re-read.
 *
 * `total` excludes cancelled containers, matching `countLines` on the server —
 * an order whose only unfinished container was cancelled is finished, not stuck
 * at "3 of 4".
 */
const applyStatusDelta = (
  entry: ContainerLinesEntry,
  from: ContainerLineStatus | null,
  to: ContainerLineStatus | null,
) => {
  if (!entry.progress || from === to) return;

  const byStatus = { ...entry.progress.byStatus };
  let total = entry.progress.total;
  let cancelled = entry.progress.cancelled ?? 0;
  let started = entry.progress.started ?? 0;

  // Everything past `planned` except the cancellation — the same set the server
  // counts as started, read off the shared table so the two cannot drift.
  const countsAsStarted = (status: ContainerLineStatus) =>
    STARTED_LINE_STATUSES.includes(status);

  if (from) {
    byStatus[from] = Math.max(0, (byStatus[from] ?? 0) - 1);
    if (from === "cancelled") cancelled = Math.max(0, cancelled - 1);
    else total = Math.max(0, total - 1);
    if (countsAsStarted(from)) started = Math.max(0, started - 1);
  }

  if (to) {
    byStatus[to] = (byStatus[to] ?? 0) + 1;
    if (to === "cancelled") cancelled += 1;
    else total += 1;
    if (countsAsStarted(to)) started += 1;
  }

  entry.progress = { ...entry.progress, byStatus, total, cancelled, started };
  entry.total = total;
};

const err = (e: any, fallback: string) =>
  e.response?.data?.message || fallback;

export const fetchContainerOrders = createAsyncThunk(
  "containerOrders/list",
  async (params: ContainerOrderFilters | undefined, { rejectWithValue }) => {
    try {
      const res = await containerOrderAPI.list(params);
      return res.data.data;
    } catch (e: any) {
      return rejectWithValue(err(e, "Failed to load container orders"));
    }
  },
);

/**
 * List column settings.
 *
 * Kept on this slice rather than beside the shipment ones, because they are
 * this list's state: the page reads them from here to draw its header, and the
 * settings dropdown writes them back through the same three calls.
 *
 * Every call returns the server's own reconciled list — which is not
 * necessarily what was sent, since the server drops ids it no longer knows and
 * appends ones the client has not heard of yet. Storing the response rather
 * than the request is what keeps the two in step.
 */
export const fetchContainerOrderColumns = createAsyncThunk(
  "containerOrders/columns/get",
  async (_: void, { rejectWithValue }) => {
    try {
      const res = await userSettingsAPI.getContainerOrderColumns();
      return res.data.data.columns as Column[];
    } catch (e: any) {
      return rejectWithValue(err(e, "Failed to load column settings"));
    }
  },
  {
    /**
     * Read once per session, not once per mount.
     *
     * A preference does not change behind the user's back — only through this
     * slice — so once it is in the store there is nothing to re-read. This
     * turns the four requests a visit was making (React's double-mount in
     * development, plus every return to the list) into one, and makes the call
     * site free to dispatch unconditionally.
     */
    condition: (_: void, { getState }) => {
      const { columnsLoaded, columnsLoading } = (
        getState() as { containerOrders: ContainerOrdersState }
      ).containerOrders;
      return !columnsLoaded && !columnsLoading;
    },
  },
);

export const updateContainerOrderColumns = createAsyncThunk(
  "containerOrders/columns/update",
  async (columns: Column[], { rejectWithValue }) => {
    try {
      const res = await userSettingsAPI.updateContainerOrderColumns(columns);
      return res.data.data.columns as Column[];
    } catch (e: any) {
      return rejectWithValue(err(e, "Failed to save column settings"));
    }
  },
);

export const resetContainerOrderColumns = createAsyncThunk(
  "containerOrders/columns/reset",
  async (_: void, { rejectWithValue }) => {
    try {
      const res = await userSettingsAPI.resetContainerOrderColumns();
      return res.data.data.columns as Column[];
    } catch (e: any) {
      return rejectWithValue(err(e, "Failed to reset column settings"));
    }
  },
);

export const fetchContainerOrder = createAsyncThunk(
  "containerOrders/get",
  async (id: string, { rejectWithValue }) => {
    try {
      const res = await containerOrderAPI.getOne(id);
      // Which of the order's charges the batch has already been invoiced for,
      // so the form can draw those rows locked from the moment it opens rather
      // than at the moment it is refused.
      return {
        order: res.data.data.order as ContainerOrder,
        servicesInvoicedOn: (res.data.data.servicesInvoicedOn ??
          {}) as ServiceInvoiceLocks,
      };
    } catch (e: any) {
      return rejectWithValue(err(e, "Failed to load container order"));
    }
  },
);

/**
 * Fetch the prefill for a copied order.
 *
 * Read-only, like the shipment list's own copy: it creates nothing and so
 * deliberately leaves `orders`/`current` alone — the caller navigates to the
 * create form and hands the result over, and the copy exists only once that
 * form is saved.
 */
export const getContainerOrderCopyData = createAsyncThunk(
  "containerOrders/copyData",
  async (id: string, { rejectWithValue }) => {
    try {
      const res = await containerOrderAPI.getCopyData(id);
      return {
        order: res.data.data.order as ContainerOrderCopyData,
        sourceOrder: res.data.data.sourceOrder as ContainerOrderCopySource,
      };
    } catch (e: any) {
      return rejectWithValue(err(e, "Failed to copy the container order"));
    }
  },
);

export const createContainerOrder = createAsyncThunk(
  "containerOrders/create",
  async (data: ContainerOrderFormData, { rejectWithValue }) => {
    try {
      const res = await containerOrderAPI.create(data);
      return res.data.data.order as ContainerOrder;
    } catch (e: any) {
      return rejectWithValue(err(e, "Failed to create container order"));
    }
  },
);

export const updateContainerOrder = createAsyncThunk(
  "containerOrders/update",
  async (
    {
      id,
      data,
    }: { id: string; data: ContainerOrderFormData & { updatedAt?: string } },
    { rejectWithValue },
  ) => {
    try {
      const res = await containerOrderAPI.update(id, data);
      /**
       * The order, and how many containers the save reached beyond it.
       *
       * Editing an order's invoicing services is the one edit that is not
       * confined to the order: the charge list is the batch's price list and is
       * rewritten on the containers already generated from it. The count is how
       * the page says so — and how it knows to refetch the containers panel,
       * which is now showing charges the server has just changed underneath it.
       */
      return {
        order: res.data.data.order as ContainerOrder,
        containersUpdated: (res.data.data.containersUpdated as number) ?? 0,
        // Re-read by the server after the save: rows have just been added and
        // removed, and the form renders the result without fetching again.
        servicesInvoicedOn: (res.data.data.servicesInvoicedOn ??
          {}) as ServiceInvoiceLocks,
      };
    } catch (e: any) {
      // 409 is the optimistic-concurrency conflict — the caller re-reads and
      // shows a reload prompt, so surface the status alongside the message.
      //
      // It is also how an invoiced charge refuses to be rewritten from the
      // order; that message names the service and the containers, so it is
      // surfaced verbatim like every other refusal here.
      return rejectWithValue({
        message: err(e, "Failed to update container order"),
        status: e.response?.status,
        /**
         * The machine-readable reason, when the server sent one.
         *
         * `SERVICES_INVOICED` is the one the form acts on rather than merely
         * reports: the charges it is holding are the refused ones, so it puts
         * the charge table back to what is stored instead of leaving the user
         * with a form that will be refused identically on every further press
         * of Save.
         */
        code: e.response?.data?.code as string | undefined,
      });
    }
  },
);

/**
 * Cancel / reopen — the only status changes a user makes directly.
 *
 * The server refuses a cancel whose children have already started moving or
 * been invoiced, so the rejection message is the user-facing explanation and is
 * surfaced verbatim rather than replaced with a generic failure.
 */
export const cancelContainerOrder = createAsyncThunk(
  "containerOrders/cancel",
  async (
    { id, updatedAt }: { id: string; updatedAt?: string },
    { rejectWithValue },
  ) => {
    try {
      const res = await containerOrderAPI.cancel(id, updatedAt);
      return res.data.data.order as ContainerOrder;
    } catch (e: any) {
      return rejectWithValue(err(e, "Failed to cancel container order"));
    }
  },
);

export const reopenContainerOrder = createAsyncThunk(
  "containerOrders/reopen",
  async (
    { id, updatedAt }: { id: string; updatedAt?: string },
    { rejectWithValue },
  ) => {
    try {
      const res = await containerOrderAPI.reopen(id, updatedAt);
      return res.data.data.order as ContainerOrder;
    } catch (e: any) {
      return rejectWithValue(err(e, "Failed to reopen container order"));
    }
  },
);


/**
 * What cancelling an order would cost, for the dialog that asks about it.
 *
 * Not stored in the slice: it is a question asked at the moment the dialog opens
 * and answered once. Keeping a preview in state would mean showing a figure
 * computed before whatever the user did in between.
 */
export const fetchCancelPreview = createAsyncThunk(
  "containerOrders/cancelPreview",
  async (id: string, { rejectWithValue }) => {
    try {
      const res = await containerOrderAPI.cancelPreview(id);
      return res.data.data as ContainerOrderCancelPreview;
    } catch (e: any) {
      return rejectWithValue(err(e, "Failed to read the cancellation summary"));
    }
  },
);

/**
 * Cancel one container.
 *
 * Returns the server's own sentence as well as the line, because it says
 * something the client cannot work out — how many board slots were handed back.
 */
export const cancelContainerLine = createAsyncThunk(
  "containerOrders/cancelLine",
  async (
    {
      orderId,
      lineId,
      reason,
      updatedAt,
    }: { orderId: string; lineId: string; reason?: string; updatedAt?: string },
    { rejectWithValue },
  ) => {
    try {
      const res = await containerOrderAPI.cancelLine(orderId, lineId, {
        reason,
        updatedAt,
      });
      return {
        line: res.data.data.line as ContainerOrderLine,
        message: res.data.message as string,
      };
    } catch (e: any) {
      return rejectWithValue(err(e, "Failed to cancel the container"));
    }
  },
);

/** Put one cancelled container back — into the waiting pool, not its old slot. */
export const restoreContainerLine = createAsyncThunk(
  "containerOrders/restoreLine",
  async (
    {
      orderId,
      lineId,
      updatedAt,
    }: { orderId: string; lineId: string; updatedAt?: string },
    { rejectWithValue },
  ) => {
    try {
      const res = await containerOrderAPI.restoreLine(orderId, lineId, updatedAt);
      return {
        line: res.data.data.line as ContainerOrderLine,
        message: res.data.message as string,
      };
    } catch (e: any) {
      return rejectWithValue(err(e, "Failed to restore the container"));
    }
  },
);

export const deleteContainerOrder = createAsyncThunk(
  "containerOrders/delete",
  async (
    { id, updatedAt }: { id: string; updatedAt?: string },
    { rejectWithValue },
  ) => {
    try {
      await containerOrderAPI.remove(id, updatedAt);
      return id;
    } catch (e: any) {
      return rejectWithValue(err(e, "Failed to delete container order"));
    }
  },
);

// ─── Lines ───────────────────────────────────────────────────────────────────

export const fetchContainerLines = createAsyncThunk(
  "containerOrders/fetchLines",
  async (
    {
      orderId,
      page,
      limit,
      search,
      handler,
    }: {
      orderId: string;
      page?: number;
      limit?: number;
      search?: string;
      /**
       * The list's handler scope, when this panel is open under a filtered row.
       *
       * Travels by the same route `search` does and for the same reason: the
       * list can show an order because *one* of its twelve containers belongs
       * to the filtered handler, and this panel is a page at a time. Dimming the
       * rest would leave the container that caused the row to appear sitting on
       * page two.
       */
      handler?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const res = await containerOrderAPI.listLines(orderId, {
        page,
        limit,
        search: search || undefined,
        handler: handler || undefined,
      });
      const data = res.data.data as {
        lines: ContainerOrderLine[];
        progress: ContainerOrderProgress;
        /** Whether the server narrowed these rows to a search. */
        matched?: boolean;
        pagination?: { page: number; total: number; pages: number };
      };
      // The page number rides back with the rows: page 1 replaces the table,
      // later pages append to it. Reading it off `meta.arg` would be the same
      // number, but this keeps the reducer working off one object.
      return { ...data, orderId, page: data.pagination?.page ?? page ?? 1 };
    } catch (e: any) {
      return rejectWithValue(err(e, "Failed to load containers"));
    }
  },
);

/**
 * One container, for its own page.
 *
 * Deliberately not routed through `linesByOrder`: that bucket is a *window* onto
 * a batch (page 1 of N, replaced whenever the panel reloads), and the container
 * page needs one specific container to stay put whether or not it happens to
 * fall inside whatever page the panel last fetched. The page owns what it loads.
 */
export const fetchContainerLine = createAsyncThunk(
  "containerOrders/fetchLine",
  async (
    { orderId, lineId }: { orderId: string; lineId: string },
    { rejectWithValue },
  ) => {
    try {
      const res = await containerOrderAPI.getLine(orderId, lineId);
      return res.data.data as {
        line: ContainerOrderLine;
        order: ContainerLineOrderRef;
      };
    } catch (e: any) {
      return rejectWithValue(err(e, "Failed to load container"));
    }
  },
);

export const generateContainerLines = createAsyncThunk(
  "containerOrders/generateLines",
  async (
    { id, payload }: { id: string; payload: GenerateLinesPayload },
    { rejectWithValue },
  ) => {
    try {
      const res = await containerOrderAPI.generateLines(id, payload);
      return res.data.data as {
        order: ContainerOrder;
        lines: ContainerOrderLine[];
      };
    } catch (e: any) {
      return rejectWithValue(err(e, "Failed to add containers"));
    }
  },
);

export const createContainerLine = createAsyncThunk(
  "containerOrders/createLine",
  async (
    { orderId, data }: { orderId: string; data: ContainerLineFormData },
    { rejectWithValue },
  ) => {
    try {
      const res = await containerOrderAPI.createLine(orderId, data);
      return res.data.data.line as ContainerOrderLine;
    } catch (e: any) {
      return rejectWithValue(err(e, "Failed to add container"));
    }
  },
);

export const updateContainerLine = createAsyncThunk(
  "containerOrders/updateLine",
  async (
    {
      orderId,
      lineId,
      data,
    }: {
      orderId: string;
      lineId: string;
      data: ContainerLineFormData & { updatedAt?: string };
    },
    { rejectWithValue },
  ) => {
    try {
      const res = await containerOrderAPI.updateLine(orderId, lineId, data);
      return res.data.data.line as ContainerOrderLine;
    } catch (e: any) {
      return rejectWithValue(err(e, "Failed to update container"));
    }
  },
);

export const deleteContainerLine = createAsyncThunk(
  "containerOrders/deleteLine",
  async (
    { orderId, lineId }: { orderId: string; lineId: string },
    { rejectWithValue },
  ) => {
    try {
      await containerOrderAPI.deleteLine(orderId, lineId);
      return lineId;
    } catch (e: any) {
      return rejectWithValue(err(e, "Failed to remove container"));
    }
  },
);

const containerOrdersSlice = createSlice({
  name: "containerOrders",
  initialState,
  reducers: {
    clearCurrentContainerOrder: (state) => {
      state.current = null;
      // Cleared with the order it describes: locks left behind would be applied
      // to the next order opened, freezing rows nobody has invoiced.
      state.currentServiceLocks = {};
    },
    clearContainerOrderError: (state) => {
      state.error = null;
    },

    /**
     * A container's message counters moved.
     *
     * Applied to whichever order's bucket holds that container, and to nothing
     * else — the payload names the container, and the buckets are keyed by
     * order, so the only cost is a walk over the batches that happen to be
     * expanded. A dispatcher with no container list open pays nothing at all.
     *
     * Patched rather than refetched on purpose: a batch of two hundred
     * containers must not be re-read from the API every time somebody in a cab
     * types a sentence. The event carries the whole of what a badge needs.
     */
    containerLineMessagingPatched: (
      state,
      action: PayloadAction<{ lineId: string; messaging: ContainerMessaging }>,
    ) => {
      const { lineId, messaging } = action.payload;

      for (const entry of Object.values(state.linesByOrder)) {
        const line = entry?.lines?.find((candidate) => candidate._id === lineId);
        if (line) {
          line.messaging = messaging;
          // A container belongs to exactly one order, so there is nothing left
          // to look for once it is found.
          break;
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchContainerOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchContainerOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.orders = action.payload.orders || [];
        state.containerMatchOrderIds =
          action.payload.containerMatchOrderIds || [];
        state.pagination = action.payload.pagination || state.pagination;
      })
      .addCase(fetchContainerOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Columns. A failure here is deliberately not written to `state.error`:
      // the list itself is fine, and the page would otherwise report a broken
      // list because a preference could not be read. The caller sees the
      // rejection and the table falls back to its default layout.
      //
      // `columnsLoaded` is set either way — it means "the question has been
      // asked", which is what the settings panel waits for before it will open.
      .addCase(fetchContainerOrderColumns.pending, (state) => {
        state.columnsLoading = true;
      })
      .addCase(fetchContainerOrderColumns.fulfilled, (state, action) => {
        state.columns = action.payload;
        state.columnsLoading = false;
        state.columnsLoaded = true;
      })
      .addCase(fetchContainerOrderColumns.rejected, (state) => {
        state.columnsLoading = false;
        state.columnsLoaded = true;
      })
      .addCase(updateContainerOrderColumns.fulfilled, (state, action) => {
        state.columns = action.payload;
      })
      .addCase(resetContainerOrderColumns.fulfilled, (state, action) => {
        state.columns = action.payload;
      })

      .addCase(fetchContainerOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchContainerOrder.fulfilled, (state, action) => {
        state.loading = false;
        state.current = action.payload.order;
        state.currentServiceLocks = action.payload.servicesInvoicedOn;
      })
      .addCase(fetchContainerOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(createContainerOrder.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(createContainerOrder.fulfilled, (state, action) => {
        state.saving = false;
        state.current = action.payload;
        state.orders.unshift(action.payload);
      })
      .addCase(createContainerOrder.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload as string;
      })

      .addCase(updateContainerOrder.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      // Merged, not replaced — same as cancel / reopen / generate below.
      //
      // `shipmentProgress` and `cancelBlockedReason` are tallied per request
      // rather than stored, and only the read endpoints attach them; a save
      // responds with the bare order. Replacing wholesale therefore dropped
      // them, and the detail page's progress card and "editing is not
      // retroactive" hint disappeared until the next reload. Carrying them
      // over is correct as well as cheaper: editing an order cannot change its
      // children's statuses, so the tallies loaded with the page still hold.
      .addCase(updateContainerOrder.fulfilled, (state, action) => {
        state.saving = false;
        const { order } = action.payload;
        state.currentServiceLocks = action.payload.servicesInvoicedOn;
        state.current = state.current ? { ...state.current, ...order } : order;
        const i = state.orders.findIndex((o) => o._id === order._id);
        if (i !== -1) state.orders[i] = { ...state.orders[i], ...order };
      })
      .addCase(updateContainerOrder.rejected, (state, action) => {
        state.saving = false;
        state.error = (action.payload as any)?.message ?? null;
      })

      .addCase(deleteContainerOrder.fulfilled, (state, action) => {
        state.orders = state.orders.filter((o) => o._id !== action.payload);
        if (state.current?._id === action.payload) state.current = null;
      })
      .addCase(deleteContainerOrder.rejected, (state, action) => {
        state.error = action.payload as string;
      })

      // ── Lines ─────────────────────────────────────────────────────────────
      //
      // Every one of these writes into `linesByOrder[orderId]`, never into a
      // shared array. Two expanded rows fetch concurrently and their responses
      // land in whichever order the network decides; keying is what stops the
      // later one from painting its containers into the earlier one's table.
      .addCase(fetchContainerLines.pending, (state, action) => {
        const entry = entryFor(state, action.meta.arg.orderId);
        entry.loading = true;
        entry.error = null;
      })
      .addCase(fetchContainerLines.fulfilled, (state, action) => {
        const { orderId, page, lines, progress, matched, pagination } =
          action.payload;
        const entry = entryFor(state, orderId);

        entry.loading = false;
        entry.error = null;
        // Page 1 replaces, later pages append. "Load more" used to replace as
        // well, so pressing it swapped the first 25 rows for the next 25 and
        // still claimed to be showing 25 of 60.
        entry.lines =
          page > 1
            ? [
                ...entry.lines.filter(
                  (existing) => !lines.some((l) => l._id === existing._id),
                ),
                ...lines,
              ]
            : lines;
        entry.loadedPages = page;
        entry.progress = progress;
        entry.total = progress?.total ?? entry.lines.length;
        // Only meaningful while the rows are a search result: cleared as soon
        // as the panel goes back to the whole batch, so a stale count cannot
        // outlive the search that produced it.
        entry.matchedTotal = matched
          ? (pagination?.total ?? entry.lines.length)
          : null;
      })
      .addCase(fetchContainerLines.rejected, (state, action) => {
        const entry = entryFor(state, action.meta.arg.orderId);
        entry.loading = false;
        entry.error = action.payload as string;
      })

      .addCase(generateContainerLines.pending, (state) => {
        state.generating = true;
        state.error = null;
      })
      .addCase(generateContainerLines.fulfilled, (state, action) => {
        state.generating = false;
        const { order } = action.payload;
        state.current = state.current
          ? { ...state.current, ...order }
          : (order as ContainerOrder);
        const i = state.orders.findIndex((o) => o._id === order._id);
        if (i !== -1) {
          state.orders[i] = { ...state.orders[i], ...order };
        }
        // The new containers are not appended here: the panel is paginated, so
        // the page they belong on depends on where the user is. Marking the
        // batch stale is what brings them in, from page 1.
        invalidateLines(state, order._id);
      })
      .addCase(generateContainerLines.rejected, (state, action) => {
        state.generating = false;
        state.error = action.payload as string;
      })

      .addCase(createContainerLine.fulfilled, (state, action) => {
        const entry = entryFor(state, action.meta.arg.orderId);
        entry.lines.push(action.payload);
        applyStatusDelta(entry, null, action.payload.status);
      })
      .addCase(createContainerLine.rejected, (state, action) => {
        state.error = action.payload as string;
      })

      // Patched in place from the response, which carries the saved container
      // in full. That is what lets an ordinary edit cost one request instead of
      // three: the row it changed is already correct, so nothing has to be
      // re-read to show it.
      .addCase(updateContainerLine.fulfilled, (state, action) => {
        const entry = entryFor(state, action.meta.arg.orderId);
        const i = entry.lines.findIndex((l) => l._id === action.payload._id);
        if (i === -1) return;
        applyStatusDelta(entry, entry.lines[i].status, action.payload.status);
        entry.lines[i] = action.payload;
      })
      .addCase(updateContainerLine.rejected, (state, action) => {
        state.error = action.payload as string;
      })

      .addCase(deleteContainerLine.fulfilled, (state, action) => {
        const entry = entryFor(state, action.meta.arg.orderId);
        const removed = entry.lines.find((l) => l._id === action.payload);
        if (removed) applyStatusDelta(entry, removed.status, null);
        entry.lines = entry.lines.filter((l) => l._id !== action.payload);
      })
      .addCase(deleteContainerLine.rejected, (state, action) => {
        state.error = action.payload as string;
      })

      /**
       * Cancelling and restoring one container, patched in place.
       *
       * The same treatment an ordinary edit gets, and for the same reason: the
       * response carries the saved container in full, so the row on screen can
       * be made correct without re-reading the batch. Without these the row kept
       * its old status until something else happened to refetch — which is
       * exactly what it did, because the thunks existed and nothing reduced
       * them.
       *
       * `applyStatusDelta` is the part that matters beyond the row. The panel's
       * header count and its progress bar are tallies, and the server's own
       * `countLines` keeps cancelled containers *out* of `total` — so cancelling
       * one has to move it from `total` into `cancelled`, and restoring has to
       * move it back. Patching only `lines` left the row correct and the counter
       * above it reading "0 container(s) in this order" over a visible row.
       */
      .addCase(cancelContainerLine.fulfilled, (state, action) => {
        const entry = entryFor(state, action.meta.arg.orderId);
        const i = entry.lines.findIndex((l) => l._id === action.payload.line._id);
        if (i === -1) return;
        applyStatusDelta(entry, entry.lines[i].status, action.payload.line.status);
        entry.lines[i] = action.payload.line;
      })
      .addCase(cancelContainerLine.rejected, (state, action) => {
        state.error = action.payload as string;
      })

      .addCase(restoreContainerLine.fulfilled, (state, action) => {
        const entry = entryFor(state, action.meta.arg.orderId);
        const i = entry.lines.findIndex((l) => l._id === action.payload.line._id);
        if (i === -1) return;
        applyStatusDelta(entry, entry.lines[i].status, action.payload.line.status);
        entry.lines[i] = action.payload.line;
      })
      .addCase(restoreContainerLine.rejected, (state, action) => {
        state.error = action.payload as string;
      })

      // Cancel and reopen behave identically from the store's point of view —
      // both return the whole order — so they share one set of handlers. These
      // are matchers rather than cases because RTK requires every addCase to
      // come first; keeping them last is what makes that legal.
      .addMatcher(
        (action) =>
          action.type === cancelContainerOrder.pending.type ||
          action.type === reopenContainerOrder.pending.type,
        (state) => {
          state.saving = true;
          state.error = null;
        },
      )
      .addMatcher(
        (action) =>
          action.type === cancelContainerOrder.fulfilled.type ||
          action.type === reopenContainerOrder.fulfilled.type,
        (state, action: { payload: ContainerOrder }) => {
          state.saving = false;
          const order = action.payload;
          state.current = state.current ? { ...state.current, ...order } : order;
          const i = state.orders.findIndex((o) => o._id === order._id);
          if (i !== -1) state.orders[i] = { ...state.orders[i], ...order };
          // Cancelling an order now closes its still-planned containers, and
          // reopening restores them, so any open panel is showing stale rows.
          invalidateLines(state, order._id);
        },
      )
      .addMatcher(
        (action) =>
          action.type === cancelContainerOrder.rejected.type ||
          action.type === reopenContainerOrder.rejected.type,
        (state, action: { payload: unknown }) => {
          state.saving = false;
          state.error = (action.payload as string) ?? null;
        },
      );
  },
});

export const {
  clearCurrentContainerOrder,
  clearContainerOrderError,
  containerLineMessagingPatched,
} = containerOrdersSlice.actions;

export default containerOrdersSlice.reducer;
